/* eslint-disable jsx-a11y/no-access-key */
import React, { CSSProperties, KeyboardEventHandler, MouseEventHandler, ReactNode } from 'react';

// Components
import { SVGIcon } from '../../atoms/SVG';
import { Hyperlink } from '../../atoms/Hyperlink';
import { Tooltip, TooltipPlacement } from '../../atoms/Tooltip';
import { useBtn } from './useBtn';
import { ButtonLink } from './Button/ButtonLink';
import { BtnText } from './partials/Text/BtnText';
import { BtnIcon } from './partials/Icon/BtnIcon';

export enum BtnModifier {
  ACTIVE = 'active',
  MICRO_INTERACTION = 'micro-interaction',
  MINOR_ACTION_ACTIVATED = 'minor-action-activated',
  MINOR_ACTION_ACTIVATED_BLUE = 'minor-action-activated-blue',
  MINOR_ACTION_ACTIVATED_DARK = 'minor-action-activated-dark',
  MINOR_ACTION_ACTIVATED_GREEN = 'minor-action-activated-green',
  MINOR_ACTION_ACTIVATED_RED = 'minor-action-activated-red',
  MINOR_ACTION_ACTIVATED_PETROL = 'minor-action-activated-petrol',
  MINOR_ACTION_FAILED = 'minor-action-failed',
  MINOR_ACTION_BUSY = 'minor-action-busy',
  MINOR_ACTION = 'minor-action',
  SINGLE_ACTION = 'single-action',
  SINGLE_ACTION_YES = 'single-action-yes',
  SINGLE_ACTION_NO = 'single-action-no',
  TRANSPARENT = 'transparent',
  WHITE = 'white',
  WHITE_BACKGROUND_FOR_DISABLED_PAGE_STATE = 'white-background-for-disabled-page-state',
  ROUND = 'round',
  TOGGLE = 'toggle',
}

export type ButtonRefType = React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>;

type BtnSpecificProps = {
  focusOnMount?: boolean;
  tooltipPlacement?: TooltipPlacement;
  svgClass?: string;
  modifier?: BtnModifier | BtnModifier[];
  disabledLink?: boolean;
  text?: string;
  titleText?: string;
  hiddenText?: boolean;
  svgSpriteId?: SVGIcon;
  routing?: boolean;
  reference?: ButtonRefType;
  ariaExpanded?: boolean;
  ariaHaspopup?: boolean;
  ariaOwns?: string;
  ariaActivedescendant?: string;
  ariaControls?: string | string[] | undefined;
  ariaCurrent?: boolean | 'false' | 'true' | 'page' | 'step' | 'location' | 'date' | 'time' | undefined;
  ariaPressed?: boolean;
  ariaDisabled?: boolean;
  ariaDescribedBy?: string;
  ariaLabel?: string;
  popoverTarget?: string;
  popoverTargetAction?: 'toggle' | 'show' | 'hide';
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onKeyUp?: KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

export type BtnType = BtnSpecificProps &
  Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    | 'type'
    | 'role'
    | 'className'
    | 'ref'
    | 'formAction'
    | 'form'
    | 'formEncType'
    | 'formMethod'
    | 'formNoValidate'
    | 'formTarget'
    | 'value'
    | 'onClick'
    | 'onKeyDown'
    | 'onKeyUp'
  > & {
    type?: 'button' | 'submit' | 'reset' | undefined;
    role?: 'button' | 'link';
    formAction?: string;
    value?: string;
    /** Anchor-specific props when href is provided */
    href?: string;
    target?: string;
  };

type BasicBtnProps = {
  ref: ButtonRefType;
  cssClasses: string;
  titleText?: string;
  role?: 'button' | 'link';
  ariaExpanded?: boolean;
  ariaControls?: string | string[] | undefined;
  ariaDescribedBy?: string;
  ariaHaspopup?: boolean;
  ariaLabel?: string;
  ariaOwns?: string;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  style?: CSSProperties;
  accessKey?: string;
  svgSpriteId?: SVGIcon;
  svgClass?: string;
  id?: string;
  hiddenText: boolean;
  text?: string;
  node?: ReactNode;
};

type BasicButtonProps = BasicBtnProps & {
  type: 'button' | 'submit' | 'reset';
  ariaDisabled?: boolean;
  disabled?: boolean;
  ariaPressed?: boolean;
  ariaActivedescendant?: string;
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onKeyUp?: KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  formAction?: string;
  value?: string;
  name?: string;
  tabIndex?: number;
  popoverTarget?: string;
  popoverTargetAction?: 'toggle' | 'show' | 'hide';
};

type BasicAnchorProps = BasicBtnProps & {
  href: string;
  rel?: string;
  target?: string;
  routing?: boolean;
  ariaCurrent?: boolean | 'false' | 'true' | 'page' | 'step' | 'location' | 'date' | 'time';
  tooltipPlacement?: TooltipPlacement;
  hideTooltipFromScreenReader: boolean;
};

const parseAriaControls = (ariaControls: string | string[] | undefined): string | undefined => {
  if (Array.isArray(ariaControls)) {
    return ariaControls.join(' ');
  }
  return ariaControls;
};

function BasicButton({
  ref,
  cssClasses,
  titleText,
  type,
  ariaDisabled,
  disabled,
  role,
  ariaExpanded,
  ariaControls,
  ariaPressed,
  ariaHaspopup,
  ariaDescribedBy,
  ariaLabel,
  ariaOwns,
  ariaActivedescendant,
  onClick,
  onKeyDown,
  onKeyUp,
  id,
  formAction,
  value,
  name,
  style,
  tabIndex,
  accessKey,
  popoverTarget,
  popoverTargetAction,
  svgSpriteId,
  svgClass,
  hiddenText,
  text,
  node,
}: BasicButtonProps) {
  return (
    <button
      ref={ref as React.RefObject<HTMLButtonElement>}
      className={cssClasses}
      title={titleText}
      // eslint-disable-next-line react/button-has-type
      type={ariaDisabled ? 'button' : type}
      disabled={disabled || undefined}
      role={role}
      aria-expanded={ariaExpanded}
      aria-controls={parseAriaControls(ariaControls)}
      aria-pressed={ariaPressed}
      aria-disabled={ariaDisabled || undefined}
      aria-haspopup={ariaHaspopup}
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      aria-owns={ariaOwns}
      aria-activedescendant={ariaActivedescendant}
      onClick={!ariaDisabled ? onClick : undefined}
      onKeyDown={!ariaDisabled ? onKeyDown : undefined}
      onKeyUp={!ariaDisabled ? onKeyUp : undefined}
      id={id}
      formAction={formAction}
      value={value}
      name={name}
      style={style}
      tabIndex={tabIndex}
      accessKey={accessKey}
      popoverTarget={popoverTarget}
      popoverTargetAction={popoverTargetAction}
      data-icon={svgSpriteId || null}
    >
      <BtnIcon svgSpriteId={svgSpriteId} svgClass={svgClass} />
      <BtnText text={text} hiddenText={hiddenText} node={node} />
    </button>
  );
}

function BasicAnchor({
  ref,
  cssClasses,
  titleText,
  href,
  rel,
  target,
  routing,
  role,
  ariaExpanded,
  ariaControls,
  ariaCurrent,
  ariaDescribedBy,
  ariaHaspopup,
  ariaLabel,
  ariaOwns,
  tooltipPlacement,
  hideTooltipFromScreenReader,
  onClick,
  style,
  accessKey,
  svgSpriteId,
  svgClass,
  id,
  hiddenText,
  text,
  node,
}: BasicAnchorProps) {
  return (
    <Hyperlink
      reference={ref}
      role={role}
      ariaExpanded={ariaExpanded}
      ariaControls={parseAriaControls(ariaControls)}
      ariaCurrent={ariaCurrent}
      ariaDescribedBy={ariaDescribedBy}
      ariaHaspopup={ariaHaspopup}
      ariaLabel={ariaLabel}
      ariaOwns={ariaOwns}
      tooltipPlacement={tooltipPlacement}
      to={href}
      rel={rel}
      routing={routing}
      className={cssClasses}
      title={titleText}
      target={target}
      id={id}
      hideTooltipFromScreenReader={hideTooltipFromScreenReader}
      onClick={onClick}
      style={style}
      accessKey={accessKey}
      dataIcon={svgSpriteId}
    >
      <BtnIcon svgSpriteId={svgSpriteId} svgClass={svgClass} />
      <BtnText text={text} hiddenText={hiddenText} node={node} />
    </Hyperlink>
  );
}

export default function Btn({
  tabIndex,
  focusOnMount = false,
  tooltipPlacement = TooltipPlacement.TOP,
  svgClass,
  modifier,
  href,
  rel,
  disabled = false,
  disabledLink = false,
  text,
  children,
  titleText,
  hiddenText = false,
  svgSpriteId,
  type = 'button',
  target,
  role,
  ariaExpanded,
  ariaHaspopup,
  ariaOwns,
  ariaActivedescendant,
  ariaControls,
  ariaCurrent,
  ariaPressed,
  ariaDisabled,
  ariaDescribedBy,
  ariaLabel,
  popoverTarget,
  popoverTargetAction,
  routing = true,
  id,
  formAction,
  value,
  name,
  style,
  reference,
  onClick,
  onKeyDown,
  onKeyUp,
  accessKey,
}: BtnType) {
  const { ref, hideTooltipFromScreenReader, cssClasses } = useBtn(reference, focusOnMount, hiddenText, modifier);

  if (disabledLink) {
    return (
      <ButtonLink
        ariaCurrent={ariaCurrent}
        ariaLabel={ariaLabel}
        cssClasses={cssClasses}
        ref={ref as React.RefObject<HTMLAnchorElement>}
        titleText={titleText}
      >
        <BtnIcon svgSpriteId={svgSpriteId} svgClass={svgClass} />
        <BtnText text={text} hiddenText={hiddenText} node={children} />
      </ButtonLink>
    );
  }

  // do we have an <a> or a real <button> ? => depends on the href attribute!
  if (href) {
    return (
      <BasicAnchor
        ref={ref}
        cssClasses={cssClasses}
        titleText={titleText}
        href={href}
        rel={rel}
        target={target}
        routing={routing}
        role={role}
        ariaExpanded={ariaExpanded}
        ariaControls={ariaControls}
        ariaCurrent={ariaCurrent}
        ariaDescribedBy={ariaDescribedBy}
        ariaHaspopup={ariaHaspopup}
        ariaLabel={ariaLabel}
        ariaOwns={ariaOwns}
        tooltipPlacement={tooltipPlacement}
        hideTooltipFromScreenReader={hideTooltipFromScreenReader}
        onClick={onClick}
        style={style}
        accessKey={accessKey}
        svgSpriteId={svgSpriteId}
        svgClass={svgClass}
        id={id}
        hiddenText={hiddenText}
        text={text}
        node={children}
      />
    );
  }

  return (
    <Tooltip attachTo={ref} placement={tooltipPlacement} hideTooltipFromScreenReader={hideTooltipFromScreenReader}>
      <BasicButton
        ref={ref}
        cssClasses={cssClasses}
        titleText={titleText}
        type={type}
        ariaDisabled={ariaDisabled}
        disabled={disabled}
        role={role}
        ariaExpanded={ariaExpanded}
        ariaControls={ariaControls}
        ariaPressed={ariaPressed}
        ariaHaspopup={ariaHaspopup}
        ariaDescribedBy={ariaDescribedBy}
        ariaLabel={ariaLabel}
        ariaOwns={ariaOwns}
        ariaActivedescendant={ariaActivedescendant}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        id={id}
        formAction={formAction}
        value={value}
        name={name}
        style={style}
        tabIndex={tabIndex}
        accessKey={accessKey}
        popoverTarget={popoverTarget}
        popoverTargetAction={popoverTargetAction}
        svgSpriteId={svgSpriteId}
        svgClass={svgClass}
        hiddenText={hiddenText}
        text={text}
        node={children}
      />
    </Tooltip>
  );
}
